import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  SPECIAL_ATTRIBUTE_COLORS,
  SpecialAttribute,
  SpecialAttributeColor,
} from "@/core/entities/special-attribute";
import {
  useCreateSpecialAttribute,
  useDeleteSpecialAttribute,
  useSpecialAttributes,
  useUpdateSpecialAttribute,
} from "@/hooks/repository-hooks/special-attribute/use-special-attribute";
import { MoreHorizontal, Plus } from "lucide-react";
import { useState } from "react";

interface SpecialAttributePoolEditorProps {
  value: string[];
  onChange: (next: string[]) => void;
}

export function SpecialAttributePoolEditor({
  value,
  onChange,
}: Readonly<SpecialAttributePoolEditorProps>) {
  const poolQuery = useSpecialAttributes();
  const createMutation = useCreateSpecialAttribute();
  const updateMutation = useUpdateSpecialAttribute();
  const deleteMutation = useDeleteSpecialAttribute();

  const pool: SpecialAttribute[] = poolQuery.data ?? [];

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState<SpecialAttributeColor>(
    SPECIAL_ATTRIBUTE_COLORS[0],
  );
  const [createError, setCreateError] = useState<string | null>(null);

  const toggle = (id: string) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) {
      setCreateError("Name is required");
      return;
    }
    const existingActive = pool.find(
      (p) => p.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (existingActive) {
      setCreateError("An attribute with this name already exists");
      return;
    }
    try {
      const newId = await createMutation.mutateAsync({
        data: { name: trimmed, color: newColor },
      } as Parameters<typeof createMutation.mutateAsync>[0]);
      onChange([...value, newId]);
      setNewName("");
      setNewColor(SPECIAL_ATTRIBUTE_COLORS[0]);
      setCreateError(null);
      setShowCreate(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create");
    }
  };

  return (
    <div className="space-y-3">
      {poolQuery.isLoading ? (
        <div className="text-xs text-muted-foreground">Loading attributes…</div>
      ) : pool.length === 0 ? (
        <div className="text-xs text-muted-foreground">
          No special attributes yet. Create one to get started.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {pool.map((entry) => {
            const checked = value.includes(entry.id);
            return (
              <div
                key={entry.id}
                onClick={() => toggle(entry.id)}
                className={
                  "flex cursor-pointer items-center justify-center gap-2 rounded-lg border px-3 py-1 transition-colors hover:bg-muted/40 " +
                  (checked ? "border-primary bg-primary/5" : "border-border")
                }
              >
                <div
                  className="h-3 w-3 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-sm font-medium">{entry.name}</span>
                <div onClick={(e) => e.stopPropagation()}>
                  <PoolEntryMenu
                    entry={entry}
                    onChange={(updates) => {
                      updateMutation.mutate({
                        id: entry.id,
                        data: updates,
                      } as Parameters<typeof updateMutation.mutate>[0]);
                    }}
                    onDelete={() => {
                      deleteMutation.mutate({ id: entry.id } as Parameters<typeof deleteMutation.mutate>[0]);
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate ? (
        <div className="space-y-2 rounded-md border p-3">
          <div className="space-y-1">
            <Label className="text-xs">Name</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. VIP Customer"
              maxLength={64}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Color</Label>
            <div className="flex flex-wrap gap-2">
              {SPECIAL_ATTRIBUTE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Pick color ${c}`}
                  onClick={() => setNewColor(c)}
                  className={
                    "h-6 w-6 rounded-full border-2 " +
                    (newColor === c ? "border-foreground" : "border-transparent")
                  }
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          {createError ? (
            <div className="text-xs text-destructive">{createError}</div>
          ) : null}
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={handleCreate}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? "Creating…" : "Create"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowCreate(false);
                setCreateError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowCreate(true)}
        >
          <Plus className="mr-1 h-4 w-4" /> New attribute
        </Button>
      )}
    </div>
  );
}

interface PoolEntryMenuProps {
  entry: SpecialAttribute;
  onChange: (
    updates: Partial<Pick<SpecialAttribute, "name" | "color">>,
  ) => void;
  onDelete: () => void;
}

function PoolEntryMenu({ entry, onChange, onDelete }: Readonly<PoolEntryMenuProps>) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(entry.name);
  const [color, setColor] = useState<SpecialAttributeColor>(
    entry.color as SpecialAttributeColor,
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-5 w-5"
          aria-label={`Edit ${entry.name}`}
        >
          <MoreHorizontal className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-2 p-2" align="end">
        {editing ? (
          <>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={64}
            />
            <div className="flex flex-wrap gap-1">
              {SPECIAL_ATTRIBUTE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Pick color ${c}`}
                  onClick={() => setColor(c)}
                  className={
                    "h-5 w-5 rounded-full border-2 " +
                    (color === c ? "border-foreground" : "border-transparent")
                  }
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => {
                  const trimmed = name.trim();
                  if (!trimmed) return;
                  onChange({ name: trimmed, color });
                  setEditing(false);
                }}
              >
                Save
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setName(entry.name);
                  setColor(entry.color as SpecialAttributeColor);
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <Button
              size="sm"
              variant="ghost"
              className="w-full justify-start"
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="w-full justify-start text-destructive"
              onClick={onDelete}
            >
              Delete
            </Button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
